// SelectButtonsField.tsx
import React from "react";
import { useFormContext, useWatch } from "react-hook-form";
import MySelectFromItems, { type SelectItem } from "../../MySelectFromItems";

interface SelectButtonsFieldProps {
  field: {
    name: string;
    label?: string;
    options?: (string | {icon?: React.ReactNode; label: string; value: string; description?: string; color?: string })[];
    optionsMap?: Record<string, (string | { label: string; value: string; description?: string })[]>;
    dependsOn?: string;
    disabled?: boolean;
    defaultValue?: string | string[];
    required?: boolean;
    helperText?: string;
    multiple?: boolean; // Enable multi-selection
    mode?: 'cards' | 'dropdown' | 'chips' | 'list' | 'radio' | 'segmented' | 'compact'; // Display mode
    searchable?: boolean; // Enable search (for dropdown mode)
    clearable?: boolean; // Show clear button
    maxDisplay?: number; // Max items to show in multi-select display
    placeholder?: string; // Placeholder text
    size?: 'sm' | 'md' | 'lg'; // Size variant
  };
}

export const SelectButtonsField: React.FC<SelectButtonsFieldProps> = ({ field }) => {
  const { setValue, getValues, watch, formState: { errors } } = useFormContext();

  // Watch dependsOn field
  const parentValue = field.dependsOn ? watch(field.dependsOn) : undefined;

  // Resolve options
  const options =
    field.dependsOn && field.optionsMap
      ? field.optionsMap[parentValue] || []
      : field.options || [];

  // Watch current value
  const value = watch(field.name);
  
  // Get error for this field
  const error = errors[field.name]?.message as string;

  // 🔑 APPLY DEFAULT ONCE
  React.useEffect(() => {
    const currentValue = getValues(field.name);
    
    if (field.defaultValue !== undefined && currentValue === undefined) {
      const defaultValue = field.defaultValue;
      
      if (field.multiple && Array.isArray(defaultValue)) {
        // For multi-select, check if all default values exist in options
        const allValid = defaultValue.every(val =>
          options.some(opt => (typeof opt === "string" ? opt : opt.value) === val)
        );
        
        if (allValid) {
          setValue(field.name, defaultValue, {
            shouldDirty: false,
            shouldTouch: false,
            shouldValidate: false,
          });
        }
      } else if (!field.multiple && typeof defaultValue === 'string') {
        // For single select
        const isValid = options.some(
          opt => (typeof opt === "string" ? opt : opt.value) === defaultValue
        );
        
        if (isValid) {
          setValue(field.name, defaultValue, {
            shouldDirty: false,
            shouldTouch: false,
            shouldValidate: false,
          });
        }
      }
    }
  }, [options, field.defaultValue, field.multiple]);

  // Map options to SelectItem[]
  const items = options.map((opt) =>
    typeof opt === "string" ? { label: opt, value: opt } : opt
  );

  // Handle value change
  const handleChange = (newValue: any) => {
    setValue(field.name, newValue, {
      shouldDirty: true,
      shouldValidate: true,
    });
  };

  return (
    <div className="w-full">
      {/* Label */}
      {field.label && (
        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
          {field.label}
          {field.required && <span className="text-red-500 dark:text-red-400 ml-1">*</span>}
        </label>
      )}
      
      {/* Select Component */}
      <MySelectFromItems
        items={items}
        value={value || (field.multiple ? [] : null)}
        onChange={handleChange}
        className="w-full"
        disabled={field.disabled}
        mode={field.mode || (field.multiple ? 'chips' : 'cards')}
        multiple={field.multiple || false}
        searchable={field.searchable}
        clearable={field.clearable}
        maxDisplay={field.maxDisplay}
        placeholder={field.placeholder || (field.multiple ? "Select options" : "Select an option")}
        size={field.size}
        error={error}
        helperText={field.helperText}
      />
    </div>
  );
};